/* 
  CustomTimer Usage:
  The <custom-timer> element displays a time duration based on a provided value in seconds.
  - Attribute `data-time`: Integer value in seconds to display (e.g., "3661").
  - Attribute `data-show`: String specifying units to display, using "Y" (years), "M" (months), "D" (days), "H" (hours), "N" (minutes), "S" (seconds).
    - Example: `data-show="M-D-H-N-S"` shows months, days, hours, minutes, seconds.
  - Attribute `data-update`: If "true", updates the timer every second.
  - Example: 
    <custom-timer data-time="3661" data-show="H-N-S" data-update="true"></custom-timer>
    Output: "1h 1min 1s" (updates live)
  - Displays "0s" if no units are shown or time is 0.
*/

class CustomTimer extends HTMLElement {
  constructor() {
    super();
    this.time = parseInt(this.getAttribute('data-time'), 10) || 0;
    this.startTime = Date.now() / 1000; // Base timestamp in seconds
    this.updateUnits();
    this.updateDisplay();
  }

  updateUnits() {
    const showValue = (this.getAttribute('data-show') || '').toUpperCase();
    this.displayUnits = {
      years: showValue.includes('Y'),
      months: showValue.includes('M'),
      days: showValue.includes('D'),
      hours: showValue.includes('H'),
      minutes: showValue.includes('N'),
      seconds: showValue.includes('S')
    };
  }

  connectedCallback() {
    this.render();
    this.setupUpdate();
  }

  disconnectedCallback() {
    if (this.updateInterval) clearInterval(this.updateInterval);
  }

  updateDisplay() {
    let remainingSeconds = this.time;
    if (this.getAttribute('data-update') === 'true') {
      const elapsed = Math.floor(Date.now() / 1000 - this.startTime);
      remainingSeconds = this.time - elapsed;
    }

    const years = Math.floor(remainingSeconds / (365 * 24 * 60 * 60));
    remainingSeconds %= 365 * 24 * 60 * 60;

    const months = Math.floor(remainingSeconds / (30 * 24 * 60 * 60));
    remainingSeconds %= 30 * 24 * 60 * 60;

    const days = Math.floor(remainingSeconds / (24 * 60 * 60));
    remainingSeconds %= 24 * 60 * 60;

    const hours = Math.floor(remainingSeconds / (60 * 60));
    remainingSeconds %= 60 * 60;

    const minutes = Math.floor(remainingSeconds / 60);
    remainingSeconds %= 60;

    const seconds = remainingSeconds;

    this.displayValues = { years, months, days, hours, minutes, seconds };
  }

  render() {
    const parts = [];
    if (this.displayUnits.years && this.displayValues.years) parts.push(`${this.displayValues.years}y`);
    if (this.displayUnits.months && this.displayValues.months) parts.push(`${this.displayValues.months}m`);
    if (this.displayUnits.days && this.displayValues.days) parts.push(`${this.displayValues.days}d`);
    if (this.displayUnits.hours && this.displayValues.hours) parts.push(`${this.displayValues.hours}h`);
    if (this.displayUnits.minutes && this.displayValues.minutes) parts.push(`${this.displayValues.minutes}min`);
    if (this.displayUnits.seconds) parts.push(`${this.displayValues.seconds}s`);

    this.textContent = parts.length ? parts.join(' ') : '0s';
  }

  setupUpdate() {
    if (this.updateInterval) return; // Prevent multiple intervals from being set

    if (this.getAttribute('data-update') === 'true') {
      const smallestUnit = this.displayUnits.seconds
        ? 1000 // Update every second
        : this.displayUnits.minutes
        ? 60 * 1000 // Update every minute
        : this.displayUnits.hours
        ? 60 * 60 * 1000 // Update every hour
        : this.displayUnits.days
        ? 24 * 60 * 60 * 1000 // Update every day
        : this.displayUnits.months
        ? 30 * 24 * 60 * 60 * 1000 // Update every month (approximation)
        : null;

      if (smallestUnit) {
        this.updateInterval = setInterval(() => {
          this.updateDisplay();
          this.render();
        }, smallestUnit);
      }
    }
  }

  static get observedAttributes() {
    return ['data-time', 'data-show', 'data-update'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'data-time') {
      this.time = parseInt(newValue, 10) || 0;
      this.startTime = Date.now() / 1000;
      this.updateDisplay();
      this.render();
    } else if (name === 'data-show') {
      this.updateUnits();
      this.render();
    } else if (name === 'data-update') {
      this.setupUpdate();
    }
  }
}

customElements.define('custom-timer', CustomTimer);